// app/frontend/components/Layout.tsx

import React, { useState, ReactNode } from 'react';
import Navbar from './Navbar/Navbar';
import Sidebar from './Sidebar';
import Footer from './Footer';
import FloatingHelpMenu from './FloatingHelpMenu';
import FlashNotificationHandler from './FlashNotificationHandler';
import { UserProvider } from '../src/context/UserContext';
import { MediaQueryProvider } from '../src/context/MediaQueryContext';

// Define layout props interface
interface LayoutProps {
  children?: ReactNode;
  hideNavbar?: boolean;
}

/**
 * The logged-out auth screens. These are single-purpose pages — one action,
 * one decision — so the site footer's "Subscribe" and "Donate a star!" CTAs
 * and the floating menu is suppressed on them. The navbar stays, since it
 * carries the Log in / Sign up switch.
 */
const AUTH_PATHS = ['/login', '/register', '/password/new', '/password/edit', '/confirmation/new'];

const Layout: React.FC<LayoutProps> = ({ children, hideNavbar = false }) => {
  const [isSidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const isLoginPage = window.location.pathname === '/login';
  // Read at render time, matching the existing isLoginPage pattern above.
  // Inertia re-renders Layout on navigation, so this stays current.
  const isAuthPage = AUTH_PATHS.includes(window.location.pathname);

  // Skip rendering navbar and sidebar if hideNavbar is true
  if (hideNavbar) {
    return (
      <MediaQueryProvider>
        <UserProvider>
          <FlashNotificationHandler />
          <div className="app-container">
            <main className="main-content">{children}</main>
            {!isAuthPage && <Footer />}
            {!isAuthPage && <FloatingHelpMenu />}
          </div>
        </UserProvider>
      </MediaQueryProvider>
    );
  }

  return (
    <MediaQueryProvider>
      <UserProvider>
        <FlashNotificationHandler />
        <div className="app-container">
          <Navbar toggleSidebar={() => setSidebarOpen((prev) => !prev)} isLoginPage={isLoginPage} />
          <Sidebar isOpen={isSidebarOpen} closeSidebar={() => setSidebarOpen(false)} />
          <main className="main-content">{children}</main>
          {!isAuthPage && <Footer />}
          {!isAuthPage && <FloatingHelpMenu />}
        </div>
      </UserProvider>
    </MediaQueryProvider>
  );
};

export default Layout;
